import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { ToastProvider } from '@/components/ui/Toaster';
// Self-hosted webfont (bundled by Vite, no third-party request).
import '@fontsource-variable/inclusive-sans';
import { ConfigProvider } from '@/contexts/config';
import { isUnauthorized } from '@/lib/errors';
import '@/lib/i18n';
import { routeTree } from './routeTree.gen';
import './styles/app.css';

// A session that ends mid-use (expiry, deactivation, password reset elsewhere)
// sends the user to sign-in instead of leaving broken pages behind.
const onAuthError = (error: unknown) => {
  if (isUnauthorized(error) && !window.location.pathname.startsWith('/login')) {
    queryClient.clear();
    void router.navigate({ to: '/login', search: { redirect: window.location.pathname, expired: true } });
  }
};

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onAuthError }),
  mutationCache: new MutationCache({ onError: onAuthError }),
  defaultOptions: {
    queries: { staleTime: 30_000, retry: (count, error) => !isUnauthorized(error) && count < 1, refetchOnWindowFocus: false }
  }
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  scrollRestoration: true
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ConfigProvider>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </ConfigProvider>
    </QueryClientProvider>
  </StrictMode>
);
